import {useEffect, useState} from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {ExportDataListItem} from './ExportDataListItem';
import {ExportSelectedNames} from '../ExportData.types';
import {Box, Typography} from '@mui/material';
import '../DataExportReport.scss';
import {useTranslation} from 'react-i18next';
import {singularizeWorkProduct} from '../../../../utils/reportUtils';

type ExportListProps = {
  items: ExportSelectedNames[];
  onReorder: (newOrder: string[]) => void;
  onRemoveItem: (id: string) => void;
};
export function ExportList({items, onReorder, onRemoveItem}: ExportListProps) {
  const {t} = useTranslation();
  const [list, setList] = useState(items);

  useEffect(() => {
    setList(items);
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (!list || list.length === 0) {
    return (
      <Box>
        <Typography
          className="export-no-items"
          data-testid={`export-list-empty-export-selection`}>
          {t('dataExportReport.emptyExportSelection')}
        </Typography>
      </Box>
    );
  }
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={list?.map((i) => i.dataId)}
        strategy={verticalListSortingStrategy}
      >
        <Box
          component="ul"
          aria-label="Selected Export Items"
          role="list"
          sx={{pl: 0, m: 0}}
        >
          {list?.map((item) => (
            <ExportDataListItem
              key={item.dataId}
              id={item.dataId}
              label={`${item.prefix ? `${singularizeWorkProduct(item.prefix)}: ` : ''}${item.label}`}
              onRemoveItem={onRemoveItem}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );

  function handleDragEnd(event: {active: any; over: any}) {
    const {active, over} = event;
    if (!over) return;
    if (active.id === over.id) return;

    const currentOrder = list?.map((i) => i.dataId);
    const oldIndex = currentOrder.indexOf(active.id);
    const newIndex = currentOrder.indexOf(over.id);

    const newOrder = arrayMove(currentOrder, oldIndex, newIndex);

    onReorder(newOrder);
  }
}
